import React, { useState, MouseEvent, useCallback } from 'react';
import { Loader } from '../Loader/Loader';
import {
  Collapse,
  IconButton,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { FaSortAmountDownAlt, FaSortAmountDown } from 'react-icons/fa';

import './Table.scss';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/all';
import { SortOrdering } from 'types/tableTypes';
import clsx from 'clsx';

export interface SortingObject {
  sortKey: string;
  sortOrdering: SortOrdering;
}
export interface TableColumns {
  key: string;
  header: string;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  formatter?: (data: any, rowData?: any) => void;
  sorter?: (a: any, b: any) => number;
  sortable?: boolean;
}

export interface CollapsibleOptions {
  idProperty: string;
  getRowData: (row: any) => any;
  columns: TableColumns[];
}
interface Props {
  idProperty: string;
  loading: boolean;
  defaultSort: SortingObject;
  columns: TableColumns[];
  data: any[];
  collapsible?: CollapsibleOptions;
  className?: string;
  borderless?: boolean;
}

const Row = ({ row, columns, collapsible }) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const collapsibleData = collapsible ? collapsible.getRowData(row) : [];
  return (
    <React.Fragment>
      <TableRow>
        {collapsible && (
          <TableCell className="collapsible-column">
            {collapsibleData && collapsibleData.length > 0 && (
              <IconButton size="small" onClick={() => setOpen(!open)}>
                {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
              </IconButton>
            )}
          </TableCell>
        )}
        {columns.map((column: TableColumns) => {
          return (
            <TableCell
              align={column.align ? column.align : 'left'}
              key={column.key}
            >
              {column.formatter
                ? column.formatter(row[column.key], row)
                : row[column.key]}
            </TableCell>
          );
        })}
      </TableRow>
      {collapsible && collapsibleData && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <TableContainer component="div" className="collapsible-table">
                <MuiTable size="small">
                  <TableHead>
                    {collapsible.columns.map((column: TableColumns) => (
                      <TableCell
                        align={column.align ? column.align : 'left'}
                        data-key={column.key}
                        key={column.key}
                      >
                        {column.header}
                      </TableCell>
                    ))}
                  </TableHead>
                  <TableBody>
                    {collapsibleData.map(data => {
                      return (
                        <TableRow key={data[collapsible.idProperty]}>
                          {collapsible.columns.map((column: TableColumns) => (
                            <TableCell
                              key={column.key}
                              align={column.align ? column.align : 'left'}
                            >
                              {column.formatter
                                ? column.formatter(data)
                                : data[column.key]}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </MuiTable>
              </TableContainer>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

/**
 * Reusable table component.
 *
 * @param {Props}
 */
const Table = ({
  idProperty,
  loading,
  defaultSort,
  columns,
  data,
  collapsible,
  borderless,
  ...props
}: Props) => {
  const [rows, setRows] = useState<any[]>(data);
  const [sortObject, setSortObject] = useState<SortingObject>(defaultSort);

  const sortMethodForTableColumn = useCallback(
    (a: any, b: any, sortBy: string) => {
      const column: TableColumns = columns.find(c => c.key === sortBy);
      return column.sorter
        ? column.sorter(a[sortBy], b[sortBy])
        : a[sortBy].localeCompare(b[sortBy]);
    },
    [columns]
  );

  const sortingByKeys = useCallback(
    (_sortingObject: SortingObject, storesToSort: any[]): any[] => {
      const sortedStores = [...storesToSort].sort((a, b) =>
        sortMethodForTableColumn(a, b, _sortingObject.sortKey)
      );
      if (_sortingObject.sortOrdering === 'desc') {
        return sortedStores.reverse();
      }
      return sortedStores;
    },
    [sortMethodForTableColumn]
  );

  /** When data is changing from parent component, we need to make sure its sorted again */
  React.useEffect(() => {
    let sortedData: any[] = [...data];
    sortedData = sortingByKeys(sortObject, sortedData);
    setRows(sortedData);
  }, [data, sortObject, sortingByKeys]);

  const setActiveSorting = (event: MouseEvent<HTMLDivElement>): void => {
    const { key } = event.currentTarget.dataset;
    let sortOrdering: SortOrdering = 'desc';

    if (sortObject.sortKey === key) {
      sortOrdering = sortObject.sortOrdering === 'asc' ? 'desc' : 'asc';
    }

    const newSortObject: SortingObject = {
      sortKey: key,
      sortOrdering,
    };
    let newStoresValue: any[] = [...rows];
    newStoresValue = sortingByKeys(newSortObject, newStoresValue);
    setRows(newStoresValue);
    setSortObject(newSortObject);
  };

  return (
    <div className={clsx({ 'table--list__container': !borderless })}>
      {loading && <Loader type="bounce" size="xxl" />}
      <TableContainer
        className={clsx({ 'table--list__table': !borderless })}
        component="div"
        style={{ maxHeight: window.innerHeight - 250 }}
      >
        <MuiTable size="small" stickyHeader className={props.className}>
          <TableHead>
            <TableRow>
              {collapsible && <TableCell className="collapsible-column" />}
              {columns.map((column: TableColumns) => (
                <TableCell
                  className={clsx({ sortable: column.sortable })}
                  align={column.align ? column.align : 'left'}
                  data-key={column.key}
                  key={column.key}
                  onClick={column.sortable && setActiveSorting}
                >
                  {column.header}
                  {sortObject.sortKey === column.key ? (
                    sortObject.sortOrdering === 'asc' ? (
                      <FaSortAmountDownAlt data-key={column.key} />
                    ) : (
                      <FaSortAmountDown data-key={column.key} />
                    )
                  ) : null}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(store => (
              <Row
                key={store[idProperty]}
                row={store}
                columns={columns}
                collapsible={collapsible}
              />
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </div>
  );
};

export default Table;
